/**
 * Prop Options - Dispatch & Delivery Estimate (v2 - dynamic profiles)
 * ---------------------------------------------------------------------
 * Same product/cart page estimate as before, but instead of one hardcoded
 * made-to-order rule, dispatch/production rules are now looked up from
 * Shopify Metaobjects (Settings > Custom data > Shipping Profile /
 * Category Mapping), fetched once per session via the Storefront API.
 *
 * This means: adding a new "type" of made-to-order product (a new lead
 * time) is done entirely in Shopify Admin - add a Shipping Profile entry
 * and a Category Mapping entry pointing a product tag at it. No code
 * change, no redeploy.
 *
 * If the Storefront API fetch ever fails (network issue, token revoked,
 * etc.), this falls back to the original hardcoded 2pm cutoff / 2-day
 * made-to-order rule below, so the page never renders with no estimate
 * at all - it just temporarily won't know about newer profiles like Pro
 * Board's 14-day lead time until the fetch succeeds again.
 *
 * Wired up by the same two Liquid snippets as before:
 *   - snippets/dispatch-estimate-product.liquid  (product page)
 *   - snippets/dispatch-estimate-cart.liquid      (cart page)
 */

(function () {
  "use strict";

  var FALLBACK_CONFIG = {
    cutoffHour: 14,
    madeToOrderProductionDays: 2
  };

  var COURIER_CONFIG = {
    nextDay: { minDays: 1, maxDays: 1, label: "Next Day Delivery" },
    standard: { minDays: 2, maxDays: 3, label: "Standard Delivery" }
  };

  var STOREFRONT_CONFIG = {
    shopDomain: "propoptions-2.myshopify.com",
    apiVersion: "2025-01",
    token: "13f1d32fda9fb5ca79d6bb3278d9c713",
    cacheKey: "propOptionsShippingConfig",
    cacheTtlMs: 10 * 60 * 1000
  };

  var DAY_MS = 24 * 60 * 60 * 1000;
  var WEEKDAY_NAMES = [
    "Sunday", "Monday", "Tuesday", "Wednesday",
    "Thursday", "Friday", "Saturday"
  ];
  var MONTH_NAMES = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];
var FORCE_MADE_TO_ORDER_TAGS = ["made-to-order-mdf"];
function hasForcedTag(tags) {
  return tags.some(function (t) { return FORCE_MADE_TO_ORDER_TAGS.indexOf(t) !== -1; });
}
  var dynamicConfig = null;
  var initPromise = null;

  function getUkParts(date) {
    var fmt = new Intl.DateTimeFormat("en-GB", {
      timeZone: "Europe/London",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false
    });
    var parts = {};
    fmt.formatToParts(date).forEach(function (p) {
      parts[p.type] = p.value;
    });
    return {
      year: parseInt(parts.year, 10),
      month: parseInt(parts.month, 10),
      day: parseInt(parts.day, 10),
      hour: parseInt(parts.hour, 10),
      minute: parseInt(parts.minute, 10)
    };
  }

  function ukDateOnly(parts) {
    return new Date(Date.UTC(parts.year, parts.month - 1, parts.day));
  }

  function isWeekday(dateOnly) {
    var day = dateOnly.getUTCDay();
    return day >= 1 && day <= 5;
  }

  function addCalendarDays(dateOnly, days) {
    return new Date(dateOnly.getTime() + days * DAY_MS);
  }

  function addBusinessDays(dateOnly, days) {
    var result = dateOnly;
    var remaining = days;
    while (remaining > 0) {
      result = addCalendarDays(result, 1);
      if (isWeekday(result)) {
        remaining--;
      }
    }
    return result;
  }

  function rollToNextWeekday(dateOnly) {
    var result = dateOnly;
    while (!isWeekday(result)) {
      result = addCalendarDays(result, 1);
    }
    return result;
  }

  function formatDateOnly(dateOnly) {
    var weekday = WEEKDAY_NAMES[dateOnly.getUTCDay()];
    var day = dateOnly.getUTCDate();
    var month = MONTH_NAMES[dateOnly.getUTCMonth()];
    return weekday + " " + day + " " + month;
  }

  function readCache() {
    try {
      var raw = sessionStorage.getItem(STOREFRONT_CONFIG.cacheKey);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || !parsed.savedAt) return null;
      if (Date.now() - parsed.savedAt > STOREFRONT_CONFIG.cacheTtlMs) return null;
      return parsed.data;
    } catch (e) {
      return null;
    }
  }

  function writeCache(data) {
    try {
      sessionStorage.setItem(
        STOREFRONT_CONFIG.cacheKey,
        JSON.stringify({ savedAt: Date.now(), data: data })
      );
    } catch (e) {
      // sessionStorage unavailable (private browsing etc) - fine
    }
  }

  function fieldValue(fields, key) {
    var match = fields.filter(function (f) { return f.key === key; })[0];
    return match ? match.value : null;
  }

  function parseShippingConfig(raw) {
    var profilesByHandle = {};
    (raw.profiles && raw.profiles.nodes ? raw.profiles.nodes : []).forEach(function (node) {
      profilesByHandle[node.handle] = {
        handle: node.handle,
        name: fieldValue(node.fields, "name"),
        type: fieldValue(node.fields, "type"),
        productionDays: parseInt(fieldValue(node.fields, "production_days"), 10) || null,
        cutoffHour: parseInt(fieldValue(node.fields, "cutoff_hour"), 10) || null
      };
    });

    var tagToProfile = {};
    (raw.mappings && raw.mappings.nodes ? raw.mappings.nodes : []).forEach(function (node) {
      var tag = fieldValue(node.fields, "shopify_tag");
      var profileField = node.fields.filter(function (f) { return f.key === "profile"; })[0];
      var handle = profileField && profileField.reference ? profileField.reference.handle : null;
      if (tag && handle && profilesByHandle[handle]) {
        tagToProfile[tag] = profilesByHandle[handle];
      }
    });

    var inStockProfile = null;
    var defaultMadeToOrderProfile = null;
    Object.keys(profilesByHandle).forEach(function (handle) {
      var profile = profilesByHandle[handle];
      if (profile.type === "In Stock" && !inStockProfile) {
        inStockProfile = profile;
      }
      if (profile.type === "Made to Order" && handle === "standard-made-to-order") {
        defaultMadeToOrderProfile = profile;
      }
    });
    if (!defaultMadeToOrderProfile) {
      Object.keys(profilesByHandle).forEach(function (handle) {
        var profile = profilesByHandle[handle];
        if (profile.type === "Made to Order" && !defaultMadeToOrderProfile) {
          defaultMadeToOrderProfile = profile;
        }
      });
    }

    return {
      tagToProfile: tagToProfile,
      inStockProfile: inStockProfile,
      defaultMadeToOrderProfile: defaultMadeToOrderProfile
    };
  }

  function fetchShippingConfig() {
    var url = "https://" + STOREFRONT_CONFIG.shopDomain + "/api/" +
      STOREFRONT_CONFIG.apiVersion + "/graphql.json";

    var query = "query ShippingConfig { " +
      "profiles: metaobjects(type: \"shipping_profile\", first: 50) { nodes { handle fields { key value } } } " +
      "mappings: metaobjects(type: \"category_mapping\", first: 50) { nodes { fields { key value reference { ... on Metaobject { handle } } } } } " +
      "}";

    return fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": STOREFRONT_CONFIG.token
      },
      body: JSON.stringify({ query: query })
    })
      .then(function (res) {
        if (!res.ok) throw new Error("Storefront API request failed: " + res.status);
        return res.json();
      })
      .then(function (json) {
        if (!json.data) throw new Error("Storefront API returned no data");
        var parsed = parseShippingConfig(json.data);
        writeCache(parsed);
        return parsed;
      });
  }

  function init() {
    if (initPromise) return initPromise;

    var cached = readCache();
    if (cached) {
      dynamicConfig = cached;
      fetchShippingConfig().then(function (fresh) {
        dynamicConfig = fresh;
      }).catch(function () {});
      initPromise = Promise.resolve(dynamicConfig);
      return initPromise;
    }

    initPromise = fetchShippingConfig()
      .then(function (fresh) {
        dynamicConfig = fresh;
        return dynamicConfig;
      })
      .catch(function () {
        dynamicConfig = null;
        return null;
      });
    return initPromise;
  }

  function calculateDispatchDate(input) {
    var now = new Date();
    var ukNow = getUkParts(now);
    var todayOnly = ukDateOnly(ukNow);
    var tags = input.tags || [];

    if (input.inStock && !hasForcedTag(tags)) {
      var cutoffHour = FALLBACK_CONFIG.cutoffHour;
      if (dynamicConfig && dynamicConfig.inStockProfile && dynamicConfig.inStockProfile.cutoffHour) {
        cutoffHour = dynamicConfig.inStockProfile.cutoffHour;
      }
      var beforeCutoff = ukNow.hour < cutoffHour;
      var todayIsWeekday = isWeekday(todayOnly);

      if (todayIsWeekday && beforeCutoff) {
        return { dispatchDate: todayOnly, madeToOrder: false };
      }
      var nextDay = addBusinessDays(todayOnly, 1);
      return { dispatchDate: nextDay, madeToOrder: false };
    }

    var productionDays = FALLBACK_CONFIG.madeToOrderProductionDays;
    if (dynamicConfig) {
      var matchedProfile = null;
      for (var i = 0; i < tags.length; i++) {
        if (dynamicConfig.tagToProfile[tags[i]]) {
          matchedProfile = dynamicConfig.tagToProfile[tags[i]];
          break;
        }
      }
      if (!matchedProfile) {
        matchedProfile = dynamicConfig.defaultMadeToOrderProfile;
      }
      if (matchedProfile && matchedProfile.productionDays) {
        productionDays = matchedProfile.productionDays;
      }
    }

    var dispatchDate = addBusinessDays(todayOnly, productionDays);
    return { dispatchDate: dispatchDate, madeToOrder: true };
  }

  function calculateDeliveryWindow(dispatchDateOnly, courierKey) {
    var courier = COURIER_CONFIG[courierKey];
    if (!courier) {
      throw new Error("Unknown courier key: " + courierKey);
    }
    var minDate = rollToNextWeekday(addBusinessDays(dispatchDateOnly, courier.minDays));
    var maxDate = rollToNextWeekday(addBusinessDays(dispatchDateOnly, courier.maxDays));
    return { minDate: minDate, maxDate: maxDate, courier: courier };
  }

  function formatDeliveryWindow(window) {
    if (window.minDate.getTime() === window.maxDate.getTime()) {
      return formatDateOnly(window.minDate);
    }
    return formatDateOnly(window.minDate) + " - " + formatDateOnly(window.maxDate);
  }

  function parseTagsAttr(value) {
    if (!value) return [];
    return value.split(",").map(function (t) { return t.trim(); }).filter(Boolean);
  }

  window.PropOptionsDispatchEstimate = {
    fallbackConfig: FALLBACK_CONFIG,
    courierConfig: COURIER_CONFIG,
    init: init,
    calculateDispatchDate: calculateDispatchDate,
    calculateDeliveryWindow: calculateDeliveryWindow,
    formatDateOnly: formatDateOnly,
    formatDeliveryWindow: formatDeliveryWindow,

    renderInto: function (el) {
      if (!el) return;
      init().then(function () {
        var tags = parseTagsAttr(el.getAttribute("data-tags"));
        var inStock = el.getAttribute("data-in-stock") === "true";

        var result = calculateDispatchDate({ tags: tags, inStock: inStock });

        var nextDay = calculateDeliveryWindow(result.dispatchDate, "nextDay");
        var standard = calculateDeliveryWindow(result.dispatchDate, "standard");

        var dispatchLabel = result.madeToOrder
          ? "Made to order, your order should be despatched by " + formatDateOnly(result.dispatchDate)
          : "In stock, your order should be despatched by " + formatDateOnly(result.dispatchDate);

        el.innerHTML =
          '<p class="dispatch-estimate__dispatch">' + dispatchLabel + "</p>" +
          '<ul class="dispatch-estimate__delivery">' +
            "<li>" + nextDay.courier.label + ": " + formatDeliveryWindow(nextDay) + "</li>" +
            "<li>" + standard.courier.label + ": " + formatDeliveryWindow(standard) + "</li>" +
          "</ul>";
      });
    }
  };
})();